import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentLateRoundedIcon from "@mui/icons-material/AssignmentLateRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";
import type { NetworkInvitationInfoJSON } from "users/networking/connections/invitations/_schemas/network-invitation-info-schema";
import NetworkInvitationQuery, {
  NetworkInvitationFilterParameters,
  NetworkInvitationOrderParameters,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import { useGetUserNetworkInvitationsQuery } from "users/networking/connections/invitations/_api/network-invitation-api";
import {
  ProposalAction,
  ProposalBoxType,
  ProposalContext,
  ProposalOrderField,
} from "work/proposals/__constants/proposal-lifecycle";
import {
  useGetAuthoredDraftProposalsCountQuery,
  useGetDraftReviewProposalsCountQuery,
  useGetInboxProposalsCountQuery,
  useGetProposalsQuery,
} from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { proposalDestination } from "work/proposals/_views/proposal-list-destination";
import { PageTab as ProposalPageTab } from "work/proposals/_views/proposals-page-tabs";

/**
 * Open invitations in both directions — the endpoint has no "addressed to me" filter, so the ones
 * this user has to answer are picked out of the page here, the way the pending tab does it. A page
 * this size is far past any real number of open invitations; if one ever ran past it the count
 * would read low rather than wrong, and the row still leads to the full list.
 */
const openNetworkInvitations = new NetworkInvitationQuery({
  filterParameters: new NetworkInvitationFilterParameters({ status: "Open" }),
  orderParameters: new NetworkInvitationOrderParameters(),
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
});

export const proposalInboxRoute = `/proposals/${ProposalPageTab.Active}?boxType=${ProposalBoxType.Inbox}`;
export const proposalDraftReviewsRoute =
  `/proposals/${ProposalPageTab.Drafts}?boxType=${ProposalBoxType.DraftReview}`;
export const proposalAuthoredDraftsRoute =
  `/proposals/${ProposalPageTab.Drafts}?boxType=${ProposalBoxType.DraftAuthored}`;
export const receivedNetworkInvitationsRoute = "/network/pending?context=received";

export function networkInvitationRoute(invitationId: string): string {
  return `/network/pending/view/${invitationId}`;
}

/**
 * How many rows the card names before it starts counting instead.
 *
 * Four is what fits the card beside its neighbours in the grid without the queue becoming the
 * page. Anything past it is summarised per kind by the buttons underneath, which lead to the same
 * three lists this card used to be made of.
 */
export const dashboardWaitingLimit = 4;

export type WaitingKind =
  | "inboxProposal"
  | "draftReviewProposal"
  | "authoredDraftProposal"
  | "networkInvitation";

export type WaitingRow = {
  key: string;
  kind: WaitingKind;
  /** The thing itself: a proposal's name, or the person who sent the invitation. */
  title: string;
  /** What is being asked of this user about it. */
  caption: string;
  to: string;
};

export type WaitingOverflow = {
  count: number;
  label: string;
  to: string;
};

function counted(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/**
 * What the proposal is actually asking of this user, read off the actions the server offers them.
 *
 * "Awaiting your response" was the only thing this card ever said, for every proposal in the inbox
 * and every unsent draft alike, which is how a reviewer's proposal came to read as though it were
 * waiting to be submitted rather than read (#5115). `availableActions` is the server's answer to
 * "what may this user do here", so the sentence and the destination are derived from one fact:
 * a row that opens the review flow says review, and a row that opens the draft editor says draft.
 *
 * The draft box it came from says the same thing a second way: `DraftReview` is the drafts this
 * user was asked to read, so a row out of it is a review whatever else the proposal offers.
 */
export function waitingCaption(kind: WaitingKind, offers: (action: ProposalAction) => boolean): string {
  if (kind === "networkInvitation") return "Invited you to connect";
  if (kind === "draftReviewProposal" || offers(ProposalAction.Review)) return "Waiting for your review";
  if (kind === "authoredDraftProposal") {
    return offers(ProposalAction.Submit) ? "Ready to submit" : "Draft you have not submitted";
  }
  // Everything else in the inbox is the other party's move handed back, whether this user answers
  // it by revising, approving or rejecting.
  return "Waiting for your response";
}

function proposalRows(
  proposals: ProposalJSON[],
  kind: WaitingKind,
  listTab: ProposalPageTab,
  context: { userId: string | undefined; isRestrictedOnboarding: boolean },
): WaitingRow[] {
  return proposals.flatMap((proposal) => {
    // A row with no id has nowhere to go, and a queue row that is not a door is not worth a line.
    if (!proposal.id) return [];
    const offers = (action: ProposalAction) =>
      (proposal.availableActions?.[action] ?? []).some((userId) => userId === context.userId);
    return [{
      key: proposal.id,
      kind,
      title: proposal.name?.trim() || "Untitled proposal",
      caption: waitingCaption(kind, offers),
      to: proposalDestination(proposal.id, offers, {
        listTab,
        isRestrictedOnboarding: context.isRestrictedOnboarding,
      }),
    }];
  });
}

/** The invitations addressed to this user; the ones they sent are somebody else's move. */
export function receivedInvitations(
  invitations: NetworkInvitationInfoJSON[] | undefined,
): NetworkInvitationInfoJSON[] {
  return (invitations ?? []).filter((invitation) => !invitation.isInviter);
}

function invitationRows(invitations: NetworkInvitationInfoJSON[]): WaitingRow[] {
  return invitations.map((invitation) => ({
    key: invitation.id,
    kind: "networkInvitation" as const,
    title: [invitation.inviter?.firstName, invitation.inviter?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "Someone in your network",
    caption: waitingCaption("networkInvitation", () => false),
    to: networkInvitationRoute(invitation.id),
  }));
}

/**
 * Every open item, most pressing first: what the other party handed back, then the drafts somebody
 * asked this user to read, then their own unsent drafts, then the people waiting to be answered.
 * The order is the one the card showed when it was counted lines, so a reader who knew where to
 * look still finds the same thing first.
 */
export function waitingRows(input: {
  inboxProposals: ProposalJSON[];
  draftReviewProposals: ProposalJSON[];
  authoredDraftProposals: ProposalJSON[];
  networkInvitations: NetworkInvitationInfoJSON[] | undefined;
  userId: string | undefined;
  isRestrictedOnboarding: boolean;
}): WaitingRow[] {
  const context = { userId: input.userId, isRestrictedOnboarding: input.isRestrictedOnboarding };
  return [
    ...proposalRows(input.inboxProposals, "inboxProposal", ProposalPageTab.Active, context),
    ...proposalRows(input.draftReviewProposals, "draftReviewProposal", ProposalPageTab.Drafts, context),
    ...proposalRows(input.authoredDraftProposals, "authoredDraftProposal", ProposalPageTab.Drafts, context),
    ...invitationRows(receivedInvitations(input.networkInvitations)),
  ];
}

/**
 * What is left over once the card has named as many rows as it can hold, per kind.
 *
 * Counting per kind rather than in one number is what keeps the overflow honest: there is no page
 * that holds proposals, drafts and invitations together, so a single "+7 more" would have had
 * nowhere to lead. Each button goes to the list its own count came from — which is why the two
 * halves of the draft box count separately, the way the drafts tab now filters them (#5115).
 */
export function waitingOverflow(
  shown: WaitingRow[],
  totals: {
    inboxProposals: number;
    draftReviewProposals: number;
    authoredDraftProposals: number;
    networkInvitations: number | undefined;
  },
): WaitingOverflow[] {
  const shownOf = (kind: WaitingKind) => shown.filter((row) => row.kind === kind).length;
  const remaining = [
    {
      count: totals.inboxProposals - shownOf("inboxProposal"),
      one: "more proposal awaiting your response",
      many: "more proposals awaiting your response",
      to: proposalInboxRoute,
    },
    {
      count: totals.draftReviewProposals - shownOf("draftReviewProposal"),
      one: "more draft awaiting your review",
      many: "more drafts awaiting your review",
      to: proposalDraftReviewsRoute,
    },
    {
      count: totals.authoredDraftProposals - shownOf("authoredDraftProposal"),
      one: "more draft you have not submitted",
      many: "more drafts you have not submitted",
      to: proposalAuthoredDraftsRoute,
    },
    {
      count: (totals.networkInvitations ?? 0) - shownOf("networkInvitation"),
      one: "more network invitation to answer",
      many: "more network invitations to answer",
      to: receivedNetworkInvitationsRoute,
    },
  ];
  // A user without network access is offered no invitation row at all, not a row reading zero.
  if (totals.networkInvitations === undefined) remaining.pop();

  return remaining
    .filter((kind) => kind.count > 0)
    .map((kind) => ({
      count: kind.count,
      label: `+${kind.count} ${counted(kind.count, kind.one, kind.many)}`,
      to: kind.to,
    }));
}

const waitingKindIcons: Record<WaitingKind, typeof AssignmentRoundedIcon> = {
  inboxProposal: AssignmentRoundedIcon,
  draftReviewProposal: RateReviewRoundedIcon,
  authoredDraftProposal: EditNoteRoundedIcon,
  networkInvitation: PersonAddAltRoundedIcon,
};

function proposalPage(boxType: ProposalBoxType, viewingAsVendor: boolean): ProposalQuery {
  return new ProposalQuery({
    filterParameters: new ProposalFilterParameters({
      boxType,
      context: viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
    }),
    orderParameters: new ProposalOrderParameters({
      orderBy: ProposalOrderField.Created,
      orderDir: OrderDirection.Descending,
    }),
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: dashboardWaitingLimit }),
  });
}

/**
 * What is actually this user's move, rather than what is merely in flight. Each row is a thing the
 * platform is holding for them: a proposal the other party has handed back, a draft somebody asked
 * them to read, a draft they have not sent, an invitation nobody has answered. Nothing here is a
 * notification about those things — the bell already carries those, and counting both would count
 * the same work twice.
 *
 * The rows name the proposal and open it, rather than counting a category and landing on a filtered
 * list (#5123). "3 proposals awaiting your response" told the reader nothing they could act on and
 * cost them two more clicks to find out; the name and the destination both come from the same list
 * response, so the row cannot promise something the proposal does not offer.
 *
 * The drafts arrive as the two boxes they are, rather than as the one box that holds both. A
 * requested reviewer joins the draft box of the party whose draft it is, so the box alone cannot
 * say whether a draft is this user's to send or somebody else's to read (#5115) — and the overflow
 * counts, which are the only category sentences left on the card, have to say which.
 *
 * This is the one card that keeps the loud marker, because every row on it is by definition the
 * reader's move.
 */
export default function WaitingOnYouCard() {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  // Network is permission-gated at the route, so a user without it must not be offered the row.
  const includesNetwork = sessionPermissions?.includesAccessToNetwork === true;
  const viewingAsVendor = session.context?.viewingAsVendor === true;

  const inboxProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(ProposalBoxType.Inbox, viewingAsVendor), [viewingAsVendor]),
  });
  const draftReviewProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(ProposalBoxType.DraftReview, viewingAsVendor), [viewingAsVendor]),
  });
  const authoredDraftProposalsQuery = useGetProposalsQuery({
    interfaceName: "dashboardWaitingOnYou",
    query: useMemo(() => proposalPage(ProposalBoxType.DraftAuthored, viewingAsVendor), [viewingAsVendor]),
  });
  const inboxCountQuery = useGetInboxProposalsCountQuery({ session });
  const draftReviewCountQuery = useGetDraftReviewProposalsCountQuery({ session });
  const authoredDraftCountQuery = useGetAuthoredDraftProposalsCountQuery({ session });
  const networkInvitationsQuery = useGetUserNetworkInvitationsQuery(openNetworkInvitations, { skip: !includesNetwork });

  const networkInvitationCount = includesNetwork
    ? receivedInvitations(networkInvitationsQuery.data?.data).length
    : undefined;

  const isLoading =
    inboxProposalsQuery.isLoading ||
    draftReviewProposalsQuery.isLoading ||
    authoredDraftProposalsQuery.isLoading ||
    inboxCountQuery.isLoading ||
    draftReviewCountQuery.isLoading ||
    authoredDraftCountQuery.isLoading ||
    networkInvitationsQuery.isLoading;
  const isError =
    inboxProposalsQuery.isError ||
    draftReviewProposalsQuery.isError ||
    authoredDraftProposalsQuery.isError ||
    inboxCountQuery.isError ||
    draftReviewCountQuery.isError ||
    authoredDraftCountQuery.isError ||
    networkInvitationsQuery.isError;

  const rows = useMemo(
    () =>
      waitingRows({
        inboxProposals: inboxProposalsQuery.data?.data ?? [],
        draftReviewProposals: draftReviewProposalsQuery.data?.data ?? [],
        authoredDraftProposals: authoredDraftProposalsQuery.data?.data ?? [],
        networkInvitations: includesNetwork ? networkInvitationsQuery.data?.data : undefined,
        userId: session.user?.id?.value,
        isRestrictedOnboarding: session.isRestrictedOnboarding,
      }),
    [
      authoredDraftProposalsQuery.data,
      draftReviewProposalsQuery.data,
      inboxProposalsQuery.data,
      includesNetwork,
      networkInvitationsQuery.data,
      session.isRestrictedOnboarding,
      session.user?.id?.value,
    ],
  );
  const visible = rows.slice(0, dashboardWaitingLimit);
  const overflow = waitingOverflow(visible, {
    inboxProposals: inboxCountQuery.data ?? 0,
    draftReviewProposals: draftReviewCountQuery.data ?? 0,
    authoredDraftProposals: authoredDraftCountQuery.data ?? 0,
    networkInvitations: networkInvitationCount,
  });
  const total =
    (inboxCountQuery.data ?? 0) +
    (draftReviewCountQuery.data ?? 0) +
    (authoredDraftCountQuery.data ?? 0) +
    (networkInvitationCount ?? 0);

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <div>
          <DashboardCardEyebrow>
            <AssignmentLateRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Your queue</Typography>
          </DashboardCardEyebrow>
          <Typography variant="h5" component="h2">
            {total > 0 ? `${total} waiting on you` : "Waiting on you"}
          </Typography>
        </div>
      </DashboardCardHeader>
      {isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading what needs your response…</Typography>
        </DashboardCardState>
      )}
      {!isLoading && isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">What needs your response could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && visible.length === 0 && (
        <DashboardCardState>
          <CheckCircleRoundedIcon color="success" fontSize="small" />
          <Typography variant="body2">Nothing is waiting on you.</Typography>
        </DashboardCardState>
      )}
      {!isLoading && !isError && visible.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {visible.map((row) => {
              const KindIcon = waitingKindIcons[row.kind];
              return (
                <DashboardRowLink key={row.key} to={row.to} aria-label={`${row.title} — ${row.caption}`}>
                  <DashboardRowMarker aria-hidden="true">
                    <KindIcon fontSize="small" />
                  </DashboardRowMarker>
                  <DashboardRowText>
                    <Typography noWrap variant="subtitle2">{row.title}</Typography>
                    <Typography color="text.secondary" noWrap variant="caption">{row.caption}</Typography>
                  </DashboardRowText>
                  <ArrowForwardRoundedIcon color="action" fontSize="small" />
                </DashboardRowLink>
              );
            })}
          </Stack>
          {overflow.length > 0 && (
            // One element, so the list's "push the last child to the bottom" rule still lines this
            // card's controls up with the cards beside it however many kinds overflowed.
            <Box>
              {overflow.map((kind) => (
                <Button key={kind.to} component={RouterLink} to={kind.to} size="small">{kind.label}</Button>
              ))}
            </Box>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}
